import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import ServiceCard from '../../components/ServiceCard/ServiceCard';
import './Services.css';

const Services = () => {
  const services = [
    {
      id: 1,
      title: 'Residential Solar Installation',
      description: 'Complete solar panel systems for homes with premium quality panels, inverters, and professional installation services.',
      image: '/images/gallery/22.jpeg',
      slug: 'residential-solar',
      features: ['Rooftop Solar Panels', 'Net Metering Setup', 'Government Subsidy Assistance', '25-Year Warranty'],
      technologies: ['Monocrystalline Panels', 'String Inverters', 'Micro Inverters', 'Power Optimizers']
    },
    {
      id: 2,
      title: 'Commercial Solar Solutions',
      description: 'Large-scale solar installations for businesses, industries, and commercial establishments to reduce operational costs.',
      image: '/images/gallery/20.jpeg',
      slug: 'commercial-solar',
      features: ['Industrial Scale Systems', 'Grid-Tie Solutions', 'Energy Management', 'ROI Analysis'],
      technologies: ['Polycrystalline Panels', 'Central Inverters', 'Monitoring Systems', 'SCADA Integration']
    },
    {
      id: 3,
      title: 'Ground Mounted Solar',
      description: 'Ground-mounted solar panels are installed on the ground using metal structures, mainly used for large-scale or open-area solar power generation.',
      image: '/images/gallery/15.jpeg',
      slug: 'ground-mounted-solar',
      features: ['Large-Scale Installation', 'Metal Frame Structures', 'Open Area Deployment', 'Optimal Sun Tracking'],
      technologies: ['Ground Mounting Systems', 'Tilt Optimization', 'Foundation Design', 'Cable Management']
    },
    {
      id: 4,
      title: 'Solar Battery Storage',
      description: 'Advanced energy storage solutions to store excess solar power for use during nighttime and power outages.',
      image: '/images/gallery/9.jpeg',
      slug: 'solar-battery-storage',
      features: ['Lithium-ion Batteries', 'Backup Power Systems', 'Load Management', 'Smart Monitoring'],
      technologies: ['LiFePO4 Batteries', 'Hybrid Inverters', 'BMS Systems', 'Mobile App Control']
    },
    {
      id: 5,
      title: 'Solar Maintenance & AMC',
      description: 'Comprehensive maintenance services and Annual Maintenance Contracts to ensure optimal performance of your solar system.',
      image: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      slug: 'solar-maintenance',
      features: ['Regular Cleaning', 'Performance Monitoring', 'Component Replacement', 'System Optimization'],
      technologies: ['Cleaning Equipment', 'Diagnostic Tools', 'Performance Analyzers', 'Remote Monitoring']
    },
    {
      id: 6,
      title: 'Solar Consulting',
      description: 'Expert consultation services for solar project planning, feasibility studies, and energy audit assessments.',
      image: '/images/gallery/14.jpeg',
      slug: 'solar-consulting',
      features: ['Site Assessment', 'Energy Audit', 'Financial Analysis', 'Permit Assistance'],
      technologies: ['Solar Irradiance Meters', 'Shading Analysis', 'Load Calculators', 'ROI Modeling']
    },
    {
      id: 7,
      title: 'Off-Grid Solar Systems',
      description: 'Complete standalone solar power systems for remote locations without grid connectivity, including battery backup.',
      image: '/images/gallery/11.jpeg',
      slug: 'off-grid-solar',
      features: ['Standalone Systems', 'Battery Backup', 'Charge Controllers', 'AC/DC Distribution'],
      technologies: ['MPPT Controllers', 'Deep Cycle Batteries', 'Pure Sine Wave Inverters', 'DC Appliances']
    },
    {
      id: 8,
      title: 'Solar Financing Solutions',
      description: 'Flexible financing options including solar loans, leasing programs, and EMI plans to make solar affordable.',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      slug: 'solar-financing',
      features: ['Zero Down Payment', 'Low Interest Loans', 'Flexible EMI Options', 'Quick Approval'],
      technologies: ['Digital Documentation', 'Online Processing', 'Credit Assessment', 'Loan Management']
    },
    {
      id: 9,
      title: 'Solar Street Lighting',
      description: 'Autonomous solar-powered LED street lighting solutions for roads, parks, and public spaces with smart controls.',
      image: 'https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/image/solar_street_light.jpg',
      slug: 'solar-street-lighting',
      features: ['LED Luminaires', 'Motion Sensors', 'Dusk to Dawn Operation', 'Weather Resistant'],
      technologies: ['High Efficiency LEDs', 'PIR Sensors', 'Lithium Batteries', 'Smart Controllers']
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Site Assessment',
      description: 'We conduct a thorough site survey to evaluate roof conditions, solar potential, and energy requirements.',
      icon: 'fas fa-home'
    },
    {
      step: '02',
      title: 'System Design',
      description: 'Our engineers design a customized solar system layout with optimal panel placement and capacity sizing.',
      icon: 'fas fa-drafting-compass'
    },
    {
      step: '03',
      title: 'Permits & Approvals',
      description: 'We handle all necessary permits, government approvals, and net metering applications for you.',
      icon: 'fas fa-file-contract'
    },
    {
      step: '04',
      title: 'Installation',
      description: 'Professional installation of solar panels, inverters, and electrical connections by certified technicians.',
      icon: 'fas fa-solar-panel'
    },
    {
      step: '05',
      title: 'Grid Connection',
      description: 'We coordinate with utility companies for grid connection and net metering setup activation.',
      icon: 'fas fa-plug'
    },
    {
      step: '06',
      title: 'Monitoring & Support',
      description: 'Ongoing system monitoring, maintenance support, and performance optimization for maximum efficiency.',
      icon: 'fas fa-chart-line'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Solar Energy Services - Complete Solar Solutions</title>
        <meta name="description" content="Explore our comprehensive range of solar energy services including residential solar, commercial installations, maintenance, energy storage, and financing options." />
        <meta name="keywords" content="solar energy services, residential solar, commercial solar, solar maintenance, energy storage, solar financing, solar panels gujarat" />
      </Helmet>

      <div className="services-page">
        {/* Hero Section */}
        <section className="services-hero">
          <div className="services-hero-overlay"></div>
          <div className="services-hero-content">
            <h1 className="services-hero-title">Services</h1>
          </div>
        </section>

        {/* Services Grid */}
        <section className="section services-grid-section">
          <div className="container">
            <div className="section-header text-center">
              <h2>What We Offer</h2>
              <p>From consultation to installation, we provide complete solar energy solutions that reduce your electricity costs.</p>
            </div>
            <div className="services-grid">
              {services.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="section section-alt process-section">
          <div className="container">
            <div className="section-header text-center">
              <h2>Our Process</h2>
              <p>A proven methodology that ensures successful project delivery.</p>
            </div>
            <div className="process-grid">
              {processSteps.map((step, index) => (
                <div key={index} className="process-step">
                  <div className="step-number">{step.step}</div>
                  <div className="step-icon">
                    <i className={step.icon}></i>
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us - Enhanced Text Design */}
        <section className="section why-choose-section-enhanced">
          <div className="container">
            <div className="why-choose-content-enhanced">
              <div className="section-intro-enhanced text-center" data-aos="fade-up">
                <div className="intro-badge-enhanced">
                  <i className="fas fa-star"></i>
                  <span>Why Choose Us</span>
                </div>
                <h2 className="main-heading-enhanced">Why Choose Global Solar Energy?</h2>
                <p className="intro-description-enhanced">
                  Discover what makes us the leading solar energy provider in Gujarat with proven expertise and customer satisfaction.
                </p>
              </div>

              <div className="benefits-grid-enhanced">
                <div className="benefit-card-enhanced" data-aos="fade-up" data-aos-delay="100">
                  <div className="benefit-header-enhanced">
                    <div className="benefit-number-enhanced">01</div>
                    <div className="benefit-icon-enhanced expert">
                      <i className="fas fa-user-tie"></i>
                    </div>
                  </div>
                  <div className="benefit-content-enhanced">
                    <h4>Expert Solar Engineers</h4>
                    <p>Our certified solar engineers have years of experience in renewable energy systems.</p>
                  </div>
                </div>
                
                <div className="benefit-card-enhanced" data-aos="fade-up" data-aos-delay="200">
                  <div className="benefit-header-enhanced">
                    <div className="benefit-number-enhanced">02</div>
                    <div className="benefit-icon-enhanced installations">
                      <i className="fas fa-solar-panel"></i>
                    </div>
                  </div>
                  <div className="benefit-content-enhanced">
                    <h4>5000+ Solar Installations</h4>
                    <p>Over 5,000 successful solar installations across Gujarat with satisfied customers.</p>
                  </div>
                </div>
                
                <div className="benefit-card-enhanced" data-aos="fade-up" data-aos-delay="300">
                  <div className="benefit-header-enhanced">
                    <div className="benefit-number-enhanced">03</div>
                    <div className="benefit-icon-enhanced technology">
                      <i className="fas fa-microchip"></i>
                    </div>
                  </div>
                  <div className="benefit-content-enhanced">
                    <h4>Premium Technology</h4>
                    <p>Australian technology smart inverters and Make in India solar panels for maximum efficiency.</p>
                  </div>
                </div>
                
                <div className="benefit-card-enhanced" data-aos="fade-up" data-aos-delay="400">
                  <div className="benefit-header-enhanced">
                    <div className="benefit-number-enhanced">04</div>
                    <div className="benefit-icon-enhanced monitoring">
                      <i className="fas fa-chart-line"></i>
                    </div>
                  </div>
                  <div className="benefit-content-enhanced">
                    <h4>24/7 Monitoring</h4>
                    <p>Round-the-clock system monitoring and maintenance support for optimal performance.</p>
                  </div>
                </div>
                
                <div className="benefit-card-enhanced" data-aos="fade-up" data-aos-delay="500">
                  <div className="benefit-header-enhanced">
                    <div className="benefit-number-enhanced">05</div>
                    <div className="benefit-icon-enhanced pricing">
                      <i className="fas fa-dollar-sign"></i>
                    </div>
                  </div>
                  <div className="benefit-content-enhanced">
                    <h4>Competitive Pricing</h4>
                    <p>Best solar solutions at competitive rates with transparent pricing and financing options.</p>
                  </div>
                </div>
                
                <div className="benefit-card-enhanced" data-aos="fade-up" data-aos-delay="600">
                  <div className="benefit-header-enhanced">
                    <div className="benefit-number-enhanced">06</div>
                    <div className="benefit-icon-enhanced installation">
                      <i className="fas fa-tools"></i>
                    </div>
                  </div>
                  <div className="benefit-content-enhanced">
                    <h4>Quick Installation</h4>
                    <p>Fast and professional installation with minimal disruption to your daily routine.</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section section-alt cta-section">
          <div className="container">
            <div className="cta-content text-center">
              <h2>Ready to Go Solar?</h2>
              <p>
                Let's discuss your energy requirements and how we can help reduce your electricity bills with solar.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary">
                  Get Solar Quote
                </Link>
                <Link to="/about" className="btn btn-outline">
                  Learn More About Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;
